import { BaseAPI } from "@utils/base-api";
import {
  CUSTOMER_MANAGEMENT_CUSTOMER_UPDATE_CUSTOMER_PATH,
  CUSTOMER_MANAGEMENT_CUSTOMER_GET_CUSTOMER_PATH,
  ABANDONED_CART_PUBLIC_ENDPOINT_PATH,
} from "@./types/customer-management/endpoint-paths";
import {
  CreateOrUpdateAbandonedCartUserResponseDto,
} from "@./types/customer-management/dto/CreateOrUpdateAbandonedCartUserResponse.dto";
import {
  CreateOrUpdateAbandonedCartUserInternalParamsDto,
} from "@./types/customer-management/dto/CreateOrUpdateAbandonedCartUserInternalParams.dto";

import {
  IUpdateCustomerDetailsRequest,
  IUpdateCustomerDetailsResponse,
  TGetUserResponse,
  TSimpleUserResponse,
} from "./types/customer.types";

export class CustomerManagementAPI extends BaseAPI {

  /**
   * Gets a user and their shipping and billing addresses via their UUID-based id
   */
  public async getFullUser(): Promise<TGetUserResponse> {
    const result = await this.axios.get(CUSTOMER_MANAGEMENT_CUSTOMER_GET_CUSTOMER_PATH);

    return result.data;
  }

  /**
   * Get a user's key information (name, email, phone number) via their UUID-based id.
   *
   * This is a simplified version of the full user response,
   * and is used for customer details pages.
   */
  public async getSimpleUser(): Promise<TSimpleUserResponse> {
    const user = await this.getFullUser();
    if (!user.firstName || !user.lastName || !user.phoneNumber) {
      throw new Error("User is missing their first name, last name, or phone number.");
    }

    return {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
    };
  }

  /**
   * Update the customer details for a given customer.
   * To be used by customers or CX via impersonation.
   *
   * @param request The request body containing the customer id
   * and the customer details
   * @returns The updated customer details.
   */
  public async updateCustomerDetails(request: IUpdateCustomerDetailsRequest): Promise<IUpdateCustomerDetailsResponse> {
    const result = await this.axios.put(
      CUSTOMER_MANAGEMENT_CUSTOMER_UPDATE_CUSTOMER_PATH,
      request,
    );

    return result.data;
  }

  /**
   * Create or update an abandoned cart user.
   * @param request The request body containing the customer details
   * @param params The parameters for the abandoned cart user
   * @returns {
   *   userId: string,
   *   legacyCustomerId: string,
   * }
   */

  public async createOrUpdateAbandonedCartUser(
    request: IUpdateCustomerDetailsRequest,
    params: CreateOrUpdateAbandonedCartUserInternalParamsDto,
  ): Promise<CreateOrUpdateAbandonedCartUserResponseDto> {
    try {
      const result = await this.axios.post(
        ABANDONED_CART_PUBLIC_ENDPOINT_PATH,
        request,
        {
          params,
        },
      );
      return result.data;
    } catch (error) {
      throw new Error("Error creating or updating abandoned cart user", { cause: error });
    }
  }

}
